import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReconOptions } from "@/components/ReconOptions";
import { api, errMsg, money, linePreview } from "@/lib/api";

const blankLine = () => ({ key: Math.random().toString(36).slice(2), product: null, quantity: 1, reconstitute: false, bac_option: "recommended", custom_bac_ml: "" });

function LineEditor({ line, idx, products, fee, onChange, onRemove }) {
  return (
    <div className="space-y-3 rounded-md border border-border p-4">
      <div className="flex gap-2">
        <Select value={line.product?.id || ""} onValueChange={(v) => onChange({ product: products.find((p) => p.id === v), reconstitute: false })}>
          <SelectTrigger data-testid={`new-order-product-select-${idx}`}><SelectValue placeholder="Choose product" /></SelectTrigger>
          <SelectContent>
            {products.map((p) => (
              <SelectItem key={p.id} value={p.id} disabled={p.stock <= 0}>{p.name} {p.vial_size} · {money(p.effective_price)} · {p.stock} left</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input type="number" min="1" value={line.quantity} className="w-20 font-mono-data" data-testid={`new-order-qty-input-${idx}`}
          onChange={(e) => onChange({ quantity: Math.max(1, Number(e.target.value) || 1) })} />
        <Button variant="ghost" size="icon" onClick={onRemove} data-testid={`new-order-remove-line-${idx}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
      </div>
      {line.product?.category === "peptide" && (
        <ReconOptions value={line} onChange={onChange} recommendedMl={line.product.recommended_bac_ml} fee={fee} quantity={line.quantity} prefix={`new-order-${idx}`} />
      )}
    </div>
  );
}

export function NewOrderDialog({ open, onClose, onCreated, presetClientId }) {
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [fee, setFee] = useState(0);
  const [clientId, setClientId] = useState(presetClientId || "");
  const [lines, setLines] = useState([blankLine()]);
  const [method, setMethod] = useState("cash");
  const [payStatus, setPayStatus] = useState("unpaid");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setClientId(presetClientId || "");
    setLines([blankLine()]);
    api.get("/admin/clients", { params: { status: "approved" } }).then((r) => setClients(r.data));
    api.get("/admin/products").then((r) => setProducts(r.data.filter((p) => p.active)));
    api.get("/admin/settings").then((r) => setFee(r.data.reconstitution_fee));
  }, [open, presetClientId]);

  const valid = lines.filter((l) => l.product);
  const total = valid.reduce((s, l) => s + linePreview(l, fee).total, 0);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/orders", {
        client_id: clientId, payment_method: method, payment_status: payStatus, notes: "",
        items: valid.map((l) => ({ product_id: l.product.id, quantity: l.quantity, reconstitute: l.reconstitute, bac_option: l.bac_option,
          custom_bac_ml: l.bac_option === "custom" && l.custom_bac_ml !== "" ? Number(l.custom_bac_ml) : null })),
      });
      toast.success(`Order ${data.order_number} created`);
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="new-order-dialog">
        <DialogHeader><DialogTitle>New order</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Client</Label>
          <Select value={clientId} onValueChange={setClientId} disabled={!!presetClientId}>
            <SelectTrigger data-testid="new-order-client-select"><SelectValue placeholder="Choose client" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.email}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-3">
          {lines.map((l, idx) => (
            <LineEditor key={l.key} line={l} idx={idx} products={products} fee={fee}
              onChange={(patch) => setLines(lines.map((x) => (x.key === l.key ? { ...x, ...patch } : x)))}
              onRemove={() => setLines(lines.filter((x) => x.key !== l.key))} />
          ))}
          <Button variant="outline" size="sm" onClick={() => setLines([...lines, blankLine()])} data-testid="new-order-add-line-button" className="gap-1"><Plus className="h-4 w-4" />Add product</Button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Payment method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="new-order-method-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bitcoin">Bitcoin</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Payment status</Label>
            <Select value={payStatus} onValueChange={setPayStatus}>
              <SelectTrigger data-testid="new-order-paystatus-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="unpaid">Unpaid</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="font-mono-data">Total <span className="text-emerald-400" data-testid="new-order-total">{money(total)}</span></span>
          <Button onClick={submit} disabled={busy || !clientId || !valid.length} data-testid="new-order-submit-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create order
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
